import { Webhook, FileText, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Brand marks for the integrations grid. Google Ads is drawn in its own
 * brand colours (a logo, not UI chrome, so the tokens-only rule doesn't
 * apply). The others are lettermarks / icons on a tinted tile.
 */
interface LogoProps {
  className?: string;
}

export function GoogleAdsLogo({ className }: LogoProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('h-12 w-12', className)} aria-hidden="true">
      {/* Yellow bar (left, tilted) */}
      <path
        d="M6.6 30.2 20.8 5.6a6.5 6.5 0 0 1 11.3 6.5L17.9 36.7a6.5 6.5 0 0 1-11.3-6.5Z"
        fill="#FBBC04"
      />
      {/* Blue bar (right, tilted) */}
      <path
        d="M27.2 12.1a6.5 6.5 0 0 1 4.9 0l9.3 16.1a6.5 6.5 0 1 1-11.3 6.5L20.8 18.6Z"
        fill="#4285F4"
      />
      {/* Green circle (bottom-left) */}
      <circle cx="12.3" cy="33.5" r="6.5" fill="#34A853" />
    </svg>
  );
}

export function SquareLogo({ className }: LogoProps) {
  // Square's mark is monochrome: a rounded square outline with a smaller
  // filled rounded square inside. Drawn in currentColor so it follows the
  // foreground token rather than introducing a palette colour.
  return (
    <div
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-xl bg-foreground/5 text-foreground',
        className,
      )}
      aria-hidden="true"
    >
      <svg viewBox="0 0 48 48" className="h-7 w-7" fill="none">
        <rect x="6" y="6" width="36" height="36" rx="7" stroke="currentColor" strokeWidth="6" />
        <rect x="18" y="18" width="12" height="12" rx="2" fill="currentColor" />
      </svg>
    </div>
  );
}

export function XeroLogo({ className }: LogoProps) {
  // Xero's mark: the lowercase wordmark in white on its brand-blue disc. A
  // logo, not UI chrome, so the brand colour is deliberate (see GoogleAdsLogo).
  return (
    <svg viewBox="0 0 48 48" className={cn('h-12 w-12', className)} aria-hidden="true">
      <circle cx="24" cy="24" r="22" fill="#13B5EA" />
      <text
        x="24"
        y="29"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
        fontSize="16"
        fontWeight="600"
        letterSpacing="-0.5"
        fill="#FFFFFF"
      >
        xero
      </text>
    </svg>
  );
}

export function CtmLogo({ className }: LogoProps) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-xl bg-info/10 text-info',
        className,
      )}
      aria-hidden="true"
    >
      <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        {/* Handset */}
        <path d="M14 8h6l3 8-4 3a20 20 0 0 0 10 10l3-4 8 3v6a4 4 0 0 1-4 4C21 38 10 27 10 12a4 4 0 0 1 4-4Z" />
        {/* Signal arcs */}
        <path d="M29 10a10 10 0 0 1 9 9" />
        <path d="M29 4a16 16 0 0 1 15 15" />
      </svg>
    </div>
  );
}

export function WebhookLogo({ className }: LogoProps) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-xl bg-warning/10 text-warning',
        className,
      )}
      aria-hidden="true"
    >
      <Webhook className="h-7 w-7" strokeWidth={2} />
    </div>
  );
}

export function EmailLogo({ className }: LogoProps) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary',
        className,
      )}
      aria-hidden="true"
    >
      <Mail className="h-7 w-7" strokeWidth={2} />
    </div>
  );
}

export function WebFormLogo({ className }: LogoProps) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-xl bg-success/10 text-success',
        className,
      )}
      aria-hidden="true"
    >
      <FileText className="h-7 w-7" strokeWidth={2} />
    </div>
  );
}
